const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', () => {
      carousel.container.setAttribute('data-initialised', 'true')
    })

    carousel.on('destroyed', () => {
      carousel.container.setAttribute('data-initialised', 'false')
    })
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', maxIdx === 0)
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', maxIdx === 0)
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

const addWheelControls = (direction) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    carousel?.container?.style?.setProperty(
      direction === 'horizontal'
        ? 'overscroll-behavior-x'
        : 'overscroll-behavior-y',
      'contain',
    )

    const dispatch = (event, name) => {
      position.x -= event.deltaX
      position.y -= event.deltaY
      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = (event) => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }
      dispatch(event, 'ksDragStart')
    }

    const wheel = (event) => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = (event) => {
      dispatch(event, 'ksDragEnd')
    }

    const eventWheel = (event) => {
      let supportWheel = true
      let earlyReturn = false
      const { details } = carousel?.track

      /* Ensure we don't block vertical scrolling for horizontal carousels */
      if (
        direction === 'horizontal' &&
        (event?.deltaY > 1 || event?.deltaY < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Ensure we don't block horizontal scrolling for vertical carousels */
      if (
        !earlyReturn &&
        direction === 'vertical' &&
        (event?.deltaX > 1 || event?.deltaX < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Prevent overscrolling for none looping carousels */
      if (!earlyReturn && !carousel.options?.loop) {
        const scrollIndex =
          direction === 'horizontal' ? event?.deltaX : event?.deltaY
        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      /* Prevent default scroll functionality for carousels supporting wheel and scrolling on the correct plane */
      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)
          wheelActive = false
          wheelEnd(event)
        }
        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }
      wheel(event)
      clearTimeout(touchTimeout)
      touchTimeout = setTimeout(() => {
        wheelActive = false
        wheelEnd(event)
      }, 150)
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })
  }
}

window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = {
  setInitialised,
  useArrows,
  useAutoplay,
  useDots,
  addWheelControls,
}
